import { Capacitor } from '@capacitor/core';

interface CapacitorRuntime {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

type WindowWithCapacitor = Window & {
  Capacitor?: CapacitorRuntime;
  capacitor?: CapacitorRuntime;
};

const isNativeCapacitorRuntime = (runtime?: CapacitorRuntime): boolean => {
  if (!runtime) {
    return false;
  }

  if (typeof runtime.isNativePlatform === 'function') {
    return runtime.isNativePlatform();
  }

  if (typeof runtime.getPlatform === 'function') {
    const platform = runtime.getPlatform();
    return platform === 'android' || platform === 'ios';
  }

  return false;
};

const isIosCapacitorRuntime = (runtime?: CapacitorRuntime): boolean => {
  if (!runtime || typeof runtime.getPlatform !== 'function') {
    return false;
  }

  return runtime.getPlatform() === 'ios';
};

const isNativeUserAgent = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent || '';

  // Capacitor Android WebView user agents commonly include `wv`.
  // Keep the fallback narrow to avoid marking normal mobile browsers as native.
  return (
    userAgent.includes('Capacitor') ||
    (/Android/i.test(userAgent) && /\bwv\b/i.test(userAgent))
  );
};

export const isCapacitorApp = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (typeof Capacitor?.isNativePlatform === 'function') {
    return Capacitor.isNativePlatform();
  }

  const runtimeWindow = window as WindowWithCapacitor;

  return (
    isNativeCapacitorRuntime(runtimeWindow.Capacitor) ||
    isNativeCapacitorRuntime(runtimeWindow.capacitor) ||
    isNativeUserAgent()
  );
};

export const isCapacitorIosApp = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (
    typeof Capacitor?.isNativePlatform === 'function' &&
    typeof Capacitor?.getPlatform === 'function'
  ) {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  }

  const runtimeWindow = window as WindowWithCapacitor;

  return (
    isIosCapacitorRuntime(runtimeWindow.Capacitor) ||
    isIosCapacitorRuntime(runtimeWindow.capacitor)
  );
};

